import * as React from 'react';
import { Switch, Route, useLocation } from 'react-router-dom';
import URLSearchParams from '@ungap/url-search-params';

import { Auth } from '@mdlinx/shared';

import { PasswordLoginForm } from './PasswordLoginForm';
import { PasswordHelpForm } from 'app/scenes/Login/PasswordHelpForm';
import { CheckInbox, PasswordResetConfirmation } from 'app/components/login-forms';
import { SocialAccountForm, SocialPopupOverlay, ProviderUserProfile } from '../SocialAccountForm';
import { RegisterAcquisitionUserError } from 'generated/client';
import { messages } from 'lib/messages';
import { trackEvent, getLocationDetails } from 'lib/analytics';
import { RegistrationTrackingData } from 'lib/tracking';
import { AutoLoginLinkForm } from './AutoLoginLinkForm';

interface LoginProps {
  onSocialSignUp: (providerUserProfile?: ProviderUserProfile) => void;
  registrationTrackingData: RegistrationTrackingData;
}

export const Login = ({ registrationTrackingData, onSocialSignUp }: LoginProps) => {
  const [isOverlayVisible, setOverlay] = React.useState(false);
  const location = useLocation();
  function onLogin() {
    if (window.parent) {
      window.parent.postMessage(Auth.loginSucceeded, '*');
    }
  }

  return (
    <Switch>
      <Route
        exact
        path={'/login'}
        render={({ history, location }) => {
          const params = new URLSearchParams(location.search);
          const acquisitionError = params.get('acquisitionError');
          const errorMessage =
            RegisterAcquisitionUserError.EmailAlreadyExists === acquisitionError
              ? messages.error.acquisition.emailAlreadyExists
              : undefined;

          return (
            <>
              <SocialPopupOverlay isOverlayVisible={isOverlayVisible} />
              <PasswordLoginForm
                errorMessage={errorMessage}
                onLogin={onLogin}
                onClickLoginHelp={() => {
                  trackEvent('auth_click', {
                    action: 'onClickLoginHelp',
                    ...getLocationDetails(location.pathname, location.search),
                  });
                  history.push('/login/help');
                }}
                socialButtons={
                  <SocialAccountForm
                    registrationTrackingData={registrationTrackingData}
                    onSocialLogin={onLogin}
                    onSocialSignUp={onSocialSignUp}
                    setLoading={setOverlay}
                  />
                }
              />
            </>
          );
        }}
      />
      <Route
        path="/login/auto-login-link"
        render={({ history, match }) => (
          <AutoLoginLinkForm
            onSuccess={(actionType: string) => history.push('/login/help/login-link-email-sent')}
            onClickBack={() => {
              trackEvent('auth_click', {
                action: 'onClickBack',
                ...getLocationDetails(location.pathname, location.search),
              });
              history.push('/login');
            }}
          />
        )}
      />
      <Route
        path={'/login/help'}
        render={({ match }) => (
          <>
            <Route
              exact
              path={`${match.path}`}
              render={({ history }) => (
                <PasswordHelpForm
                  onClickBack={() => {
                    trackEvent('auth_click', {
                      action: 'onClickBack',
                      ...getLocationDetails(location.pathname, location.search),
                    });
                    history.push('/login');
                  }}
                  onSuccess={(actionType: string) =>
                    history.push(
                      actionType === 'password-reset'
                        ? `${match.path}/password-reset-email-sent`
                        : `${match.path}/login-link-email-sent`,
                    )
                  }
                />
              )}
            />
            <Route
              path={`${match.path}/password-reset-email-sent`}
              render={({ history }) => (
                <CheckInbox
                  onClickBack={() => {
                    trackEvent('auth_click', {
                      action: 'onClickBack',
                      ...getLocationDetails(location.pathname, location.search),
                    });
                    history.push('/login');
                  }}
                />
              )}
            />
            <Route
              path={`${match.path}/login-link-email-sent`}
              render={({ history }) => (
                <CheckInbox
                  onClickBack={() => {
                    trackEvent('auth_click', {
                      action: 'onClickBack',
                      ...getLocationDetails(location.pathname, location.search),
                    });
                    history.push('/login');
                  }}
                  messageBody="An email has been sent to the address you provided with an auto-login link."
                />
              )}
            />
            <Route
              path={`${match.path}/password-reset-confirmation`}
              render={() => <PasswordResetConfirmation onClickClose={onLogin} />}
            />
          </>
        )}
      />
    </Switch>
  );
};
